import api from 'api'
import {
  WORK_ORDER_CACHE_INVALIDATION_TAGS,
  conditionalWorkOrderTags,
  withDelayedWorkOrderInvalidation,
} from 'api/cacheInvalidationTagSets'
import {
  CACHE_TAG_CONFIGURED_SERVICE_DETAILS,
  CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST,
  CACHE_TAG_CONFIGURED_SERVICE_GENERAL_FEES,
  CACHE_TAG_CONFIGURED_SERVICE_TAXES,
  CACHE_TAG_CONFIGURED_SERVICE_VERSION_LIST,
  CACHE_TAG_CONFIGURED_SERVICES_LIST,
  CACHE_TAG_CONTAINER_VERSION_LIST,
  CACHE_TAG_EVENTS_VERSION_LIST,
  CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
} from 'api/cacheTagTypes'
import { withIdTag } from 'utils/cache'

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    searchPricingServices: build.query({
      query: ({ page, size, search, ...rest }) => ({
        url: '/api/configured-service/pricing-service/search',
        body: {
          requestedPage: page,
          requestedPageSize: size,
          search,
          ...rest,
        },
        method: 'POST',
      }),
    }),
    fetchConfiguredServiceDetails: build.query({
      query: ({ id }) => ({
        url: `/api/v1/core/configured-service/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONFIGURED_SERVICE_DETAILS, arg?.id),
    }),
    fetchConfiguredServiceEvents: build.query({
      query: ({ id }) => ({
        url: `/api/v1/core/configured-service/${id}/events`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST, arg?.id),
    }),
    createConfiguredServiceEvents: build.mutation({
      query: ({ id, body }) => ({
        url: `/api/v1/core/configured-service/${id}/events`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_EVENTS_VERSION_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_VERSION_LIST, arg?.id),
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...conditionalWorkOrderTags(arg),
      ],
      onQueryStarted: withDelayedWorkOrderInvalidation({ api }),
    }),
    updateConfiguredServiceEvents: build.mutation({
      query: ({ id, eventId, body }) => ({
        url: `/api/v1/core/configured-service/${id}/events/${eventId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_EVENTS_VERSION_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_VERSION_LIST, arg?.id),
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...WORK_ORDER_CACHE_INVALIDATION_TAGS,
      ],
      onQueryStarted: withDelayedWorkOrderInvalidation({ api }),
    }),
    deleteConfiguredServiceEvents: build.mutation({
      query: ({ id, eventId }) => ({
        url: `/api/v1/core/configured-service/${id}/events/${eventId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_DETAILS, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_EVENTS_VERSION_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_VERSION_LIST, arg?.id),
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...WORK_ORDER_CACHE_INVALIDATION_TAGS,
      ],
      onQueryStarted: withDelayedWorkOrderInvalidation({ api }),
    }),
    updateConfiguredService: build.mutation({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured to omit it from the request body
      query: ({ id, skipWorkOrderInvalidation, ...rest }) => ({
        url: `/api/v1/core/configured-service/${id}`,
        method: 'PUT',
        body: { ...rest },
      }),
      invalidatesTags: (result, error, arg) => [
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_DETAILS, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_VERSION_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_TAXES, arg?.id),
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...conditionalWorkOrderTags(arg),
      ],
      // Recurrence / service-detail updates trigger async WO regeneration on the backend.
      onQueryStarted: withDelayedWorkOrderInvalidation({ api }),
    }),
    getConfiguredServiceGeneralFees: build.query({
      query: ({ id }) => ({
        url: `/api/v1/core/configured-service/${id}/general-fees`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONFIGURED_SERVICE_GENERAL_FEES, arg?.id),
    }),
    updateConfiguredServiceGeneralFees: build.mutation({
      query: ({ id, generalFees }) => ({
        url: `/api/v1/core/configured-service/${id}/general-fees`,
        method: 'PUT',
        body: generalFees,
      }),
      // Fees affect WO totals/line items — refresh WO details and calendar.
      invalidatesTags: (result, error, arg) => [
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_GENERAL_FEES, arg?.id),
        ...WORK_ORDER_CACHE_INVALIDATION_TAGS,
      ],
    }),
    getConfiguredServiceTaxes: build.query({
      query: ({ id }) => ({
        url: `/api/v1/core/configured-service/${id}/taxes`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONFIGURED_SERVICE_TAXES, arg?.id),
    }),
    updateConfiguredServiceTaxes: build.mutation({
      query: ({ id, taxes }) => ({
        url: `/api/v1/core/configured-service/${id}/taxes`,
        method: 'PUT',
        body: taxes,
      }),
      // Tax changes affect WO totals — refresh WO details and calendar.
      invalidatesTags: (result, error, arg) => [
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_TAXES, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_VERSION_LIST, arg?.id),
        ...WORK_ORDER_CACHE_INVALIDATION_TAGS,
      ],
    }),
  }),
})

export const {
  useLazySearchPricingServicesQuery,
  useFetchConfiguredServiceDetailsQuery,
  useLazyFetchConfiguredServiceDetailsQuery,
  useLazyFetchConfiguredServiceEventsQuery,
  useCreateConfiguredServiceEventsMutation,
  useUpdateConfiguredServiceEventsMutation,
  useDeleteConfiguredServiceEventsMutation,
  useUpdateConfiguredServiceMutation,
  useLazyGetConfiguredServiceGeneralFeesQuery,
  useUpdateConfiguredServiceGeneralFeesMutation,
  useLazyGetConfiguredServiceTaxesQuery,
  useUpdateConfiguredServiceTaxesMutation,
} = extendedApi
